<template>
  <PForm
    ref="pForm"
    :heading="formHeading"
    :primary-cta="submitButton"
    :secondary-cta="secondaryCta"
    :show-footer="formState.preview"
    @close="closeForm"
    @submit.prevent="onSubmit"
    @reset="resetForm"
  >
    <div v-if="!formState.preview" class="border-none q-ma-none q-pa-none">
      <div class="q-mb-md">
        <span class="q-mr-xxs">Status:</span>
        <span
          :class="
            formData.status_cd === 'active' ? 'text-info-50' : 'text-warning-70'
          "
        >
          {{
            formData?.status_cd
              ? useChangeCase(formData?.status_cd, "capitalCase")?.value
              : "-"
          }}
        </span>
      </div>
    </div>
    <q-separator v-if="!formState.preview" class="q-mb-xl q-mt-sm" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div :class="[formState.preview ? 'col-6' : 'col-4']">
          <PInput
            v-model.trim="formData.number"
            label="Credit Note No."
            :placeholder="formState.preview ? 'Not generated' : 'Enter value'"
            required
            :rules="[validateNumber]"
            :loading="validatingNumber"
            debounce="300"
            :disable="Boolean(formState.view || formState.preview)"
          />
        </div>

        <div :class="[formState.preview ? 'col-6' : 'col-4']">
          <PVDateInput
            v-model="formData.date"
            label="Credit Note Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="Boolean(formState.view || formState.preview)"
            :min-date="currentDate"
            :max-date="currentDate"
          />
        </div>

        <div v-if="formState.preview" class="col-6">
          <PInput v-model.trim="formData.status" label="CN Status" disable />
        </div>
        <div v-if="formState.preview" class="col-6">
          <PInput v-model.trim="formData.stage" label="CN Stage" disable />
        </div>

        <div v-if="!formState.preview" class="col-4">
          <PInput
            :model-value="
              formData.cn_for === 'bill' ? '' : formData.po_id.payment_terms
            "
            label="Payment Terms"
            placeholder="Enter value"
            disable
          />
        </div>

        <div v-if="!formState.preview" class="col-6">
          <div class="row">
            <template
              v-for="({ label, value }, index) in creditNoteInfo"
              :key="index"
            >
              <div v-if="label" class="col-4">
                <span class="text-neutral-80">{{ label }}</span>
              </div>
              <div v-if="label" class="col-8">
                <PBadge
                  v-if="label === 'CN Against'"
                  :label="value || '-'"
                  :type="value"
                  class="text-capitalize"
                />
                <span v-else class="text-neutral text-wrap">
                  {{ value || "-" }}
                </span>
              </div>
            </template>
          </div>
        </div>
        <div v-if="!formState.preview" class="col-6">
          <div class="row">
            <div class="col-4">
              <span class="text-neutral-80">PO No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">{{ formData.po_info?.number }}</span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">Poshn PO No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{ formData.po_info?.internal_number }}
              </span>
            </div>
            <div v-if="formData.cn_for === 'invoice'" class="col-4">
              <span class="text-neutral-80">
                <span v-if="isQCGeneratedCreditNote">QC</span>
                <span v-else>POD</span> File</span
              >
            </div>
            <div v-if="formData.cn_for === 'invoice'" class="col-8">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.[fileType]?.length"
                :files="files?.[fileType]"
              />
              <span v-else>-</span>
            </div>
            <div v-if="isPODStatusInfoVisible" class="col-4">
              <span class="text-neutral-80">POD Status</span>
            </div>
            <div v-if="isPODStatusInfoVisible" class="col-8">
              <PBadge
                v-if="formData?.pod_id?.status"
                :label="
                  formData?.pod_id?.status &&
                  useChangeCase(formData?.pod_id?.status, 'capitalCase')?.value
                "
                :type="
                  formData?.pod_id?.status &&
                  useChangeCase(formData?.pod_id?.status, 'capitalCase')?.value
                "
              />
              <span v-else class="text-neutral-80">-</span>
            </div>
            <div v-if="formData.cn_for === 'bill'" class="col-4">
              <span class="text-neutral-80">PB File</span>
            </div>
            <div v-if="formData.cn_for === 'bill'" class="col-8">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['Purchase Bill']?.length"
                :files="files?.['Purchase Bill']"
              />
              <span v-else>-</span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">CN File(s)</span>
            </div>
            <div class="col-8">
              <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
              <PMap
                v-else-if="files?.['Credit Note']?.length"
                :files="files?.['Credit Note']"
              />
              <span v-else>-</span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">E-invoice File(s)</span>
            </div>
            <div class="col-8">
              <div v-if="formData.cn_for === 'invoice'">
                <p v-if="loadingFiles" class="q-mb-none">Loading File...</p>
                <PMap
                  v-else-if="files?.['E-Invoice']?.length"
                  :files="files?.['E-Invoice']"
                />
                <span v-else>-</span>
              </div>
              <div v-else>
                <span>Only available for CN against Invoice</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div v-if="!formState.preview" class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6 column">
          <PInput
            :model-value="billing_address"
            class="col"
            label="Bill To"
            placeholder="Enter customer's billing address"
            type="textarea"
            autogrow
            disable
            required
          />
        </div>
        <div class="col-6 column">
          <PInput
            :model-value="shipping_address"
            class="col"
            label="Ship To"
            placeholder="Enter customer's shipping address"
            type="textarea"
            autogrow
            disable
            required
          />
        </div>
      </div>
    </div>
    <q-separator
      v-if="!(formState.view || formState.preview)"
      class="q-mb-xl q-mt-sm"
    />
    <div v-if="!(formState.view || formState.preview)" class="overflow-hidden">
      <div class="row">
        <div class="col-12">
          <PFile
            v-model="file"
            label="Attach Proof of Document (Credit Note)"
            help="Allowed file types are PDFs, images, excel, csv"
            required
          />
          <PFile
            v-if="file"
            v-model="file"
            class="q-mt-xxs"
            variant="re-upload"
          />
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="overflow-hidden">
      <legend class="q-mb-sm text-neutral text-weight-medium text-md">
        Items
      </legend>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :columns="productListColumns"
        :pagination="{
          rowsPerPage: 25,
        }"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        class="text-xs"
        table-header-class="text-neutral-70"
        dense
        flat
        wrap-cells
      >
        <template #body-cell="props">
          <q-td :key="props.col.name" :props="props">
            <PEditable
              v-model="props.row[props.col.name]"
              :type="props.col.type"
              disable
            />
          </q-td>
        </template>

        <template #body-cell-name="props">
          <q-td :key="props.col.name" :props="props">
            <ItemNameCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-qty="props">
          <q-td :key="props.col.name" :props="props">
            <ItemQuantityCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-rate="props">
          <q-td :key="props.col.name" :props="props">
            <ItemRateCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-amount="props">
          <q-td :key="props.col.name" :props="props">
            <ItemAmountCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-gst_exempted="props">
          <q-td :key="props.col.name" :props="props">
            <CheckMarkIcon
              v-if="props.row.gst_exempted"
              class="icon--sm text-primary"
            />
            <span v-else>-</span>
          </q-td>
        </template>
      </PTable>
      <span v-else class="text-neutral-50">No Items</span>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div :class="[formState.preview ? 'col-12' : 'col-6']">
          <PInput
            v-model.trim="formData.remarks"
            label="Remarks (if any)"
            placeholder="Enter remarks"
            type="textarea"
            autogrow
            :disable="Boolean(formState.view || formState.preview)"
          />
        </div>
        <div v-if="!formState.preview" class="col-6">
          <div class="row">
            <div class="col-6">
              <p class="q-mb-none">Total Amount</p>
              <span class="text-neutral-70 text-xs"> Excludes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: getTotalAmountWithoutTax(),
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div class="row q-mt-lg">
            <div class="col-6">
              <p class="q-mb-none">Tax Total</p>
              <span class="text-neutral-70 text-xs"> GST + CESS </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div class="row q-mt-lg">
            <div class="col-6">
              <p class="q-mb-none">Grand Total</p>
              <span class="text-xs text-neutral-70"> Includes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div
            v-if="formData.cn_for === 'invoice' && formData.tcs"
            class="row q-mt-lg"
          >
            <div class="col-6">
              <span class="text-neutral">
                TCS ({{ formData.tcs.percentage }}%)
              </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.tcs.amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div
            v-if="formData.cn_for === 'invoice' && formData.tcs"
            class="row q-mt-lg"
          >
            <div class="col-6">
              <p class="q-mb-none">Net Payable</p>
              <span class="text-xs text-neutral-70"> Check DN Separately </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount + (formData.tcs?.amount ?? 0),
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="formState.preview" #footer-info>
      <InfoPopover :info="netPayableInfo">
        <p class="q-mb-none">
          <span class="block text-neutral-80 text-xxs">Net Payable</span>
          <span class="text-sm">
            {{
              numberFormat({ number: formData.total_amount, style: "currency" })
            }}
          </span>
        </p>
      </InfoPopover>
    </template>
  </PForm>
</template>

<script setup>
import { date, useQuasar } from "quasar";
import CheckMarkIcon from "assets/icons/checkmark.svg";
import AutoGenIconUrl from "assets/icons/auto-gen.svg?url";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import creditNoteFormModel from "src/modules/accounts/models/creditNoteForm";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import * as fileApi from "src/api/file";
import { numberFormat } from "assets/scripts/utilities/helpers";
import { useProductList } from "src/modules/purchase-order/composables/product-list";
import { notify } from "assets/scripts/utilities/api-clients";

const $q = useQuasar();

const { formatDate } = date;

const pForm = ref(null);
const currentDate = new Date();

const { formState, creditNote } = storeToRefs(useCreditNoteStore());

const formHeading = computed(() => {
  let heading = "Credit Note";

  if (unref(formState).preview) {
    return `Preview ${heading}`;
  }

  if (unref(formState).view) {
    return `View ${heading}`;
  }

  return `Add ${heading}`;
});

const {
  autoGenerateCreditNote,
  getCreditNotes,
  postCreditNote,
  putCreditNote,
  isDuplicate,
  toggleCreditNoteForm,
} = useCreditNoteStore();

watchEffect(() => {
  if (unref(formState).open && unref(creditNote).stage === "Not Started") {
    unref(creditNote).date = new Date();
  }
});

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: creditNoteFormModel(),
  entity: creditNote,
  formRef: pForm,
  isForm: formState,
  post: postCreditNote,
  put: putCreditNote,
  toggleForm: toggleCreditNoteForm,
});

const netPayableInfo = computed(() => {
  return [
    {
      label: "Total Amount (exc. tax)",
      value: unref(formData).total_amount - unref(formData).total_tax,
    },
    {
      label: "Total Tax",
      value: unref(formData).total_tax,
    },
    {
      label: "Grand Total",
      value: unref(formData).total_amount,
    },
    {
      label: `TCS (${unref(formData).tcs?.percentage}%)`,
      value: unref(formData).tds?.amount,
    },
  ];
});

const { getTotalAmountWithoutTax } = useProductList({
  formData,
});

const creditNoteInfo = computed(() => {
  const {
    bill_date,
    bill_number,
    cn_for,
    invc_date,
    invc_id,
    invc_number,
    po_info,
    seller,
    vehicle_number,
  } = unref(formData);

  return [
    {
      label: "CN Against",
      value: cn_for === "bill" ? "Purchase Bill" : cn_for,
    },
    {
      label: cn_for === "bill" ? "PB No." : "Invoice No.",
      value: cn_for === "bill" ? bill_number : invc_number,
    },
    {
      label: cn_for === "bill" ? "PB Date" : "Invoice Date",
      value:
        cn_for === "bill"
          ? formatDate(bill_date, "D MMM YYYY")
          : formatDate(invc_date, "D MMM YYYY"),
    },
    {
      label: "Vehicle No.",
      value: vehicle_number,
    },
    {
      label: cn_for === "bill" && "Vendor",
      value: seller?.shop_name,
    },
    {
      label: cn_for === "bill" && "Vendor GSTIN",
      value: seller?.gstin,
    },
    {
      label: "Customer",
      value: po_info?.buyer_name || invc_id?.po_info?.buyer_name,
    },
    {
      label: "Customer GSTIN",
      value: po_info?.buyer_gstin || invc_id?.po_info?.buyer_gstin,
    },
  ];
});

const files = ref({});
const loadingFiles = ref(false);

const validatingNumber = ref(false);
const isDuplicateCNNumber = ref(false);

const shipping_address = computed(() => {
  if (unref(formData)?.shipping_address) {
    const shop_name = unref(formData)?.shipping_address_info?.shop_name
      ? `${unref(formData)?.shipping_address_info?.shop_name}\n`
      : "";

    return `${shop_name}${unref(formData)?.shipping_address}`;
  }

  return "";
});

const billing_address = computed(() => {
  if (unref(formData)?.billing_address) {
    const shop_name = unref(formData)?.billing_address_info?.shop_name
      ? `${unref(formData)?.billing_address_info?.shop_name}\n`
      : "";

    return `${shop_name}${unref(formData)?.billing_address}`;
  }

  return "";
});

const getFiles = async () => {
  try {
    loadingFiles.value = true;

    const { _id, bill_id, invc_id, pod_id, qc_id } = unref(formData);
    const status_cd = "active";
    const fileGetters = [];

    files.value = {};

    if (_id) {
      fileGetters.push(
        fileApi.index({
          params: {
            cn_id: _id,
            status_cd,
          },
        }),
      );
    }

    if (bill_id?._id || bill_id) {
      fileGetters.push(
        fileApi.index({
          params: {
            bill_id: bill_id?._id || bill_id,
            status_cd,
          },
        }),
      );
    }

    if (invc_id?._id) {
      fileGetters.push(
        fileApi.index({
          params: {
            invc_id: invc_id._id,
            status_cd,
            fileType: "E-Invoice",
          },
        }),
      );
    }

    if (pod_id) {
      fileGetters.push(
        fileApi.index({
          params: {
            pod_id: pod_id._id || pod_id,
            status_cd,
          },
        }),
      );
    }

    if (qc_id) {
      fileGetters.push(
        fileApi.index({
          params: {
            qc_id,
            fileType: "Quality Check",
            status_cd,
          },
        }),
      );
    }

    const response = await Promise.all(fileGetters);

    for (const entity of response) {
      let entityFiles = entity?.data?.data?.data;

      if (entityFiles?.length) {
        unref(files)[entityFiles[0]?.file_type] = entityFiles;
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    loadingFiles.value = false;
  }
};

const { isRightDrawerOpen } = storeToRefs(useAppStore());

const validateNumber = async (value) => {
  try {
    validatingNumber.value = true;

    isDuplicateCNNumber.value = false;

    const payload = { number: value, _id: unref(formData)._id };

    await isDuplicate(payload);

    validatingNumber.value = false;

    return true;
  } catch (error) {
    validatingNumber.value = false;

    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      isDuplicateCNNumber.value = true;

      return "Credit Note number already exists";
    }

    return true;
  }
};

watchEffect(() => {
  if (unref(isRightDrawerOpen)) {
    getFiles();
  }
});

const productListColumns = computed(() => {
  let columns = [
    {
      align: "left",
      name: "name",
      label: "Name",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: "Qty",
      field: "qty",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
  ];

  return columns;
});

const submitButton = computed(() => {
  if (unref(formState).view) {
    return {};
  }

  if (unref(formState).preview) {
    if (unref(formData).number) {
      return {};
    }

    return unref(oneClickGenerateButton);
  }

  return {
    loading: unref(isLoading),
    disable: unref(validatingNumber) || unref(isDuplicateCNNumber),
  };
});

const { getPod } = usePodStore();
const { pod } = storeToRefs(usePodStore());

let isAutoGenerating = ref(false);

const onConfirmAutoGeneration = async () => {
  try {
    isAutoGenerating.value = true;

    await autoGenerateCreditNote(unref(formData));

    if (unref(formState).preview) {
      await getPod({ id: unref(pod)._id });
    } else {
      await getCreditNotes();
    }

    closeForm(true);
  } catch (error) {
    console.error(error);
  } finally {
    isAutoGenerating.value = false;
  }
};

const confirmAutoGeneration = () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      labelClass: "text-center q-ma-xl rounded-borders",
      text: "Are you sure want to proceed?",
      onConfirm: onConfirmAutoGeneration,
    },
  });
};

const oneClickGenerateButton = computed(() => {
  return {
    disable: unref(formData)?.invc_id?.automation,
    icon: `img:${AutoGenIconUrl}`,
    label: "1-Click generate",
    loading: unref(isAutoGenerating),
    onClick: confirmAutoGeneration,
    outline: true,
    type: "button",
  };
});

const secondaryCta = computed(() => {
  if (unref(formState).view || unref(formState).preview) {
    return {};
  }

  if (unref(formData).stage === "Not Started") {
    return unref(oneClickGenerateButton);
  }

  return { visible: true };
});

const { getCounts } = useAccountStore();
const { file, uploadFile } = useFile(formData);

const onSubmit = async () => {
  const submission = await submitForm(false);

  isLoading.value = true;

  await uploadFile({ type: "Credit Note", showMessage: false });

  await Promise.all([getCounts(), getCreditNotes()]);

  isLoading.value = false;

  closeForm(true);

  notify({
    type: "positive",
    message: submission?.data?.message,
  });
};

const isQCGeneratedCreditNote = computed(() => {
  return Boolean(unref(formData).qc_id);
});

const fileType = computed(() => {
  if (unref(isQCGeneratedCreditNote)) {
    return "Quality Check";
  }

  return "Proof of Delivery";
});

const isPODStatusInfoVisible = computed(() => {
  if (unref(isQCGeneratedCreditNote)) {
    return false;
  }

  return unref(formData.cn_for) === "invoice";
});
</script>
